import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ListView } from '../../../design-system/list-view/ListView'
import { RowActionsMenu } from '../../../design-system/list-view/RowActionsMenu'
import type { Density, ExportFormat, SortSpec } from '../../../design-system/list-view/types'
import { usePersistentState } from '../../../lib/usePersistentState'
import { useDebouncedValue } from '../../../lib/useDebouncedValue'
import { useSavedViews } from '../../../lib/savedViews'
import { rsqlAnd, rsqlIn, rsqlSearch } from '../../../lib/rsql'
import { runExport, exportCapNote, type ExportColumn } from '../../../lib/export'
import { saveCloneProvenance } from '../../../lib/cloneProvenance'
import { listPrograms } from '../../../api/cartridgeStudio'
import { ApiError } from '../../../api/problem'
import type { ClonedProgramView, ProgramView } from '../../../types/cartridgeStudio'
import { PROGRAM_COLUMN_ORDER, buildProgramColumns } from './programColumns'
import { NewProgramDialog } from './NewProgramDialog'
import { CloneProgramDialog } from './CloneProgramDialog'

const PAGE_LIMIT = 50

interface FacetState {
  domains: Set<string>
  levels: Set<string>
}

function emptyFacets(): FacetState {
  return { domains: new Set(), levels: new Set() }
}

interface SerializedFacetState {
  domains: string[]
  levels: string[]
}

interface ProgramsSavedViewState {
  facets: SerializedFacetState
  search: string
  sort: SortSpec[]
  columnOrder: string[]
  hiddenColumnIds: string[]
  pinnedColumnIds: string[]
  density: Density
}

function buildFilter(facets: FacetState, search: string): string | null {
  const domainClause = rsqlIn('kind', [...facets.domains])
  const levelClause = rsqlIn('level', [...facets.levels])
  const searchClause = rsqlSearch(['jurisdiction', 'code'], search)
  return rsqlAnd(domainClause, levelClause, searchClause) || null
}

function buildSort(sort: SortSpec[]): string | null {
  if (sort.length === 0) return null
  return sort.map((s) => (s.direction === 'desc' ? `-${s.columnId}` : s.columnId)).join(',')
}

/**
 * `/admin/cartridge-studio` — IB-210 Slice 2 (demo v66, design doc §3, DoR §3 AC1). PlatformAdmin
 * canon-authoring landing screen: `GET /api/v1/programs`, real filter/sort/search, row activate →
 * the per-program workspace. Empty/error states are explicit per AC1 — never a blank screen.
 */
export function ProgramsGridScreen() {
  const navigate = useNavigate()
  const columns = useMemo(() => buildProgramColumns(), [])

  const [columnOrder, setColumnOrder] = usePersistentState<string[]>('iw.cartridgeStudio.programs.columnOrder', PROGRAM_COLUMN_ORDER)
  const [hiddenColumnIdsArr, setHiddenColumnIdsArr] = usePersistentState<string[]>('iw.cartridgeStudio.programs.hiddenColumns', [])
  const [pinnedColumnIdsArr, setPinnedColumnIdsArr] = usePersistentState<string[]>('iw.cartridgeStudio.programs.pinnedColumns', [
    'jurisdiction',
  ])
  const [density, setDensity] = usePersistentState<Density>('iw.cartridgeStudio.programs.density', 'compact')

  const hiddenColumnIds = useMemo(() => new Set(hiddenColumnIdsArr), [hiddenColumnIdsArr])
  const pinnedColumnIds = useMemo(() => new Set(pinnedColumnIdsArr), [pinnedColumnIdsArr])

  const [facets, setFacets] = useState<FacetState>(emptyFacets)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search, 250)
  const [sort, setSort] = useState<SortSpec[]>([{ columnId: 'createdAt', direction: 'desc' }])
  const [selection, setSelection] = useState<Set<string>>(new Set())

  const [newProgramOpen, setNewProgramOpen] = useState(false)
  const [cloneSource, setCloneSource] = useState<ProgramView | null>(null)

  const [programs, setPrograms] = useState<ProgramView[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const filterString = useMemo(() => buildFilter(facets, debouncedSearch), [facets, debouncedSearch])
  const sortString = useMemo(() => buildSort(sort), [sort])
  const latestRequestRef = useRef(0)

  const fetchPage = useCallback(
    async (afterCursor: string | null, replace: boolean) => {
      const requestId = ++latestRequestRef.current
      try {
        if (replace) {
          setLoading(true)
          setError(null)
        } else {
          setLoadingMore(true)
        }
        const page = await listPrograms({ filter: filterString, sort: sortString, cursor: afterCursor, limit: PAGE_LIMIT })
        if (latestRequestRef.current !== requestId) return
        setPrograms((prev) => (replace ? page.items : [...prev, ...page.items]))
        setCursor(page.nextCursor)
      } catch (err) {
        if (latestRequestRef.current !== requestId) return
        setError(err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not load programs. Check your connection and try again.')
      } finally {
        if (latestRequestRef.current === requestId) {
          setLoading(false)
          setLoadingMore(false)
        }
      }
    },
    [filterString, sortString],
  )

  useEffect(() => {
    setSelection(new Set())
    void fetchPage(null, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterString, sortString])

  function retry() {
    void fetchPage(null, true)
  }

  const quickFilters = useMemo(
    () => [
      { id: 'all', label: 'All' },
      { id: 'domain:film', label: 'Film & Media' },
      { id: 'domain:rnd', label: 'R&D' },
    ],
    [],
  )
  const activeQuickFilterIds = useMemo(() => {
    const ids = new Set<string>()
    if (facets.domains.size === 0) ids.add('all')
    for (const d of facets.domains) ids.add(`domain:${d}`)
    return ids
  }, [facets])

  function onQuickFilterToggle(id: string) {
    if (id === 'all') {
      setFacets(emptyFacets())
      return
    }
    if (id.startsWith('domain:')) {
      const domain = id.slice('domain:'.length)
      setFacets((prev) => {
        const domains = new Set(prev.domains)
        if (domains.has(domain)) domains.delete(domain)
        else domains.add(domain)
        return { ...prev, domains }
      })
    }
  }

  const activeFilterChips = useMemo(() => {
    const chips: { id: string; label: string }[] = []
    for (const level of facets.levels) chips.push({ id: `level:${level}`, label: `Level: ${level}` })
    return chips
  }, [facets])

  function removeFilterChip(id: string) {
    const [kind, value] = id.split(':', 2) as [string, string]
    if (kind === 'level') {
      setFacets((prev) => ({ ...prev, levels: new Set([...prev.levels].filter((l) => l !== value)) }))
    }
  }

  function clearAllFilters() {
    setFacets(emptyFacets())
    setSearch('')
  }

  const hasAnyFilterOrSearch = facets.domains.size > 0 || facets.levels.size > 0 || debouncedSearch.trim() !== ''

  function toggleColumnHidden(id: string) {
    setHiddenColumnIdsArr((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]))
  }
  function moveColumn(id: string, direction: -1 | 1) {
    setColumnOrder((prev) => {
      const index = prev.indexOf(id)
      const target = index + direction
      if (index === -1 || target < 0 || target >= prev.length) return prev
      const next = [...prev]
      const tmp = next[index]!
      next[index] = next[target]!
      next[target] = tmp
      return next
    })
  }
  function toggleColumnPin(id: string) {
    setPinnedColumnIdsArr((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]))
  }

  const savedViewState: ProgramsSavedViewState = useMemo(
    () => ({
      facets: { domains: [...facets.domains], levels: [...facets.levels] },
      search,
      sort,
      columnOrder,
      hiddenColumnIds: hiddenColumnIdsArr,
      pinnedColumnIds: pinnedColumnIdsArr,
      density,
    }),
    [facets, search, sort, columnOrder, hiddenColumnIdsArr, pinnedColumnIdsArr, density],
  )
  const applySavedViewState = useCallback((state: ProgramsSavedViewState) => {
    setFacets({ domains: new Set(state.facets.domains), levels: new Set(state.facets.levels) })
    setSearch(state.search)
    setSort(state.sort)
    setColumnOrder(state.columnOrder)
    setHiddenColumnIdsArr(state.hiddenColumnIds)
    setPinnedColumnIdsArr(state.pinnedColumnIds)
    setDensity(state.density)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const savedViews = useSavedViews<ProgramsSavedViewState>('cartridgeStudio.programs', savedViewState, applySavedViewState)

  const exportColumns: ExportColumn<ProgramView>[] = useMemo(
    () =>
      columnOrder
        .map((id) => columns.find((c) => c.id === id))
        .filter((c): c is NonNullable<typeof c> => c !== undefined && !hiddenColumnIds.has(c.id))
        .map((c) => ({ id: c.id, header: c.header, toText: c.toExportText })),
    [columnOrder, hiddenColumnIds, columns],
  )

  function handleExport(format: ExportFormat) {
    runExport(format, 'Programs', programs, exportColumns)
  }

  /**
   * IB-285/IB-211 S2 (FR-M7.4) — the clone response IS the provenance manifest (backend never
   * projects `cloned_from_program_id` back through `GET /programs/{id}` yet — see
   * `lib/cloneProvenance.ts`'s header note), so it is captured here, at the one moment it is
   * available, before navigating straight to the new draft's workspace.
   */
  function handleCloned(cloned: ClonedProgramView) {
    saveCloneProvenance(cloned.program.id, {
      sourceProgramId: cloned.sourceProgramId,
      sourceJurisdiction: cloneSource?.jurisdiction ?? '',
      sourceLevel: cloneSource?.level ?? '',
      sourceCode: cloneSource?.code ?? '',
      sourceStructureVersion: cloned.sourceStructureVersion,
      asOf: cloned.asOf,
      clonedAt: new Date().toISOString(),
    })
    setCloneSource(null)
    navigate(`/admin/cartridge-studio/programs/${cloned.program.id}`)
  }

  const state: 'loading' | 'error' | 'empty-tenant' | 'empty-filtered' | 'ready' = error
    ? 'error'
    : loading
      ? 'loading'
      : programs.length === 0
        ? hasAnyFilterOrSearch
          ? 'empty-filtered'
          : 'empty-tenant'
        : 'ready'

  return (
    <>
      <ListView<ProgramView>
        entityIcon="edit-3"
        title="Programs"
        countLabel={loading ? undefined : `${programs.length}${cursor ? '+' : ''}`}
        primaryAction={{ label: 'New program', icon: 'plus', onClick: () => setNewProgramOpen(true) }}
        quickFilters={quickFilters}
        activeQuickFilterIds={activeQuickFilterIds}
        onQuickFilterToggle={onQuickFilterToggle}
        search={search}
        onSearchChange={setSearch}
        searchLabel="Search programs"
        searchPlaceholder="Search by jurisdiction or code…"
        filterChildren={
          <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
            <legend className="iw-field-label" style={{ marginBottom: 4 }}>
              Level
            </legend>
            {['national', 'federal', 'state', 'provincial'].map((level) => (
              <label key={level} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--iw-size-sm)', padding: '2px 0' }}>
                <input
                  type="checkbox"
                  checked={facets.levels.has(level)}
                  onChange={() =>
                    setFacets((prev) => {
                      const levels = new Set(prev.levels)
                      if (levels.has(level)) levels.delete(level)
                      else levels.add(level)
                      return { ...prev, levels }
                    })
                  }
                />
                {level}
              </label>
            ))}
          </fieldset>
        }
        activeFilterCount={activeFilterChips.length}
        activeFilterChips={activeFilterChips}
        onRemoveFilterChip={removeFilterChip}
        onClearFilters={clearAllFilters}
        savedViews={savedViews}
        onExport={handleExport}
        exportNote={exportCapNote(programs.length, Boolean(cursor))}
        columns={columns}
        columnOrder={columnOrder}
        hiddenColumnIds={hiddenColumnIds}
        pinnedColumnIds={pinnedColumnIds}
        onToggleColumnHidden={toggleColumnHidden}
        onMoveColumn={moveColumn}
        onToggleColumnPin={toggleColumnPin}
        sort={sort}
        onSortChange={setSort}
        density={density}
        onDensityChange={setDensity}
        state={state}
        rows={programs}
        getRowId={(row) => row.id}
        onRowActivate={(row) => navigate(`/admin/cartridge-studio/programs/${row.id}`)}
        renderRowActions={(row) => (
          <RowActionsMenu
            label={`Actions for ${row.jurisdiction} ${row.code}`}
            actions={[
              { id: 'open', label: 'Open', icon: 'chevron-right', onSelect: () => navigate(`/admin/cartridge-studio/programs/${row.id}`) },
              { id: 'clone', label: 'Clone program…', icon: 'copy', onSelect: () => setCloneSource(row) },
            ]}
          />
        )}
        selection={selection}
        onSelectionChange={setSelection}
        bulkActions={[]}
        errorMessage={error ?? undefined}
        onRetry={retry}
        emptyTenant={{
          icon: 'database',
          title: 'No programs yet',
          description:
            'Programs are the canon jurisdiction/level/code root of every cartridge — none exist yet in this environment.',
          action: (
            <button type="button" className="iw-btn iw-btn--primary" onClick={() => setNewProgramOpen(true)}>
              New program
            </button>
          ),
        }}
        hasMore={Boolean(cursor)}
        onLoadMore={() => void fetchPage(cursor, false)}
        loadingMore={loadingMore}
      />

      {newProgramOpen ? (
        <NewProgramDialog
          onClose={() => setNewProgramOpen(false)}
          onCreated={(program) => {
            setNewProgramOpen(false)
            navigate(`/admin/cartridge-studio/programs/${program.id}`)
          }}
        />
      ) : null}

      {cloneSource ? (
        <CloneProgramDialog sourceProgram={cloneSource} onClose={() => setCloneSource(null)} onCloned={handleCloned} />
      ) : null}
    </>
  )
}
